import { z } from 'zod';

/** What one person may do on a conversation. `reader` is someone who can see it
 *  through the project's visibility but was never invited. */
export const ConversationRoleSchema = z.enum(['owner', 'editor', 'viewer', 'reader']);
export type ConversationRoleValue = z.infer<typeof ConversationRoleSchema>;

export const CollaboratorSchema = z.object({
  user_email: z.string(),
  role: z.enum(['viewer', 'editor']),
  invited_by: z.string(),
  created_at: z.string(),
});
export type Collaborator = z.infer<typeof CollaboratorSchema>;

/** One person currently on the conversation. */
export const PresenceMemberSchema = z.object({
  user_email: z.string(),
  /** Index into the client palette, assigned server-side and stable per sitting. */
  color_index: z.number(),
  /** Workspace tab they are on, or null when unknown. */
  active_tab: z.string().nullable(),
  /** Field they have focused, e.g. a surface cell. Null when nothing is focused. */
  focus_key: z.string().nullable(),
  /** Whether this person currently holds the pen. */
  is_edit_admin: z.boolean(),
});
export type PresenceMember = z.infer<typeof PresenceMemberSchema>;

/** The caller's standing plus who else is here. Returned by the heartbeat, so
 *  one request both reports position and polls the room. */
export const CollabStateSchema = z.object({
  role: ConversationRoleSchema,
  /** Whether the caller's edits land directly rather than becoming proposals. */
  is_edit_admin: z.boolean(),
  /** Who holds the pen, or null when nobody eligible is present. */
  edit_admin: z.string().nullable(),
  roster: z.array(PresenceMemberSchema),
  collaborators: z.array(CollaboratorSchema),
  pending_proposals: z.number(),
});
export type CollabState = z.infer<typeof CollabStateSchema>;

/** One field a proposal would change, as shown in the review dialog. */
export const ProposalChangeSchema = z.object({
  /** e.g. "Surface 2 · radius". */
  label: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
});
export type ProposalChange = z.infer<typeof ProposalChangeSchema>;

export const EditProposalSchema = z.object({
  id: z.string(),
  conversation_id: z.string(),
  author_email: z.string(),
  kind: z.enum(['system', 'merit', 'design_spec', 'zosapi_script']),
  base_version: z.number().nullable(),
  note: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected']),
  created_at: z.string(),
  decided_at: z.string().nullable(),
  decided_by: z.string().nullable(),
  /** True when the artifact has moved past base_version, so approving would
   *  overwrite newer work. Computed at read time, never stored. */
  stale: z.boolean(),
  /** What the proposal would change, field by field, so a reviewer approves
   *  something they can see rather than a note they must trust. Computed with
   *  the same differ the write path uses. Empty on endpoints that do not load
   *  the current design. */
  changes: z.array(ProposalChangeSchema),
});
export type EditProposal = z.infer<typeof EditProposalSchema>;

export const EditProposalListSchema = z.object({
  proposals: z.array(EditProposalSchema),
});
export type EditProposalList = z.infer<typeof EditProposalListSchema>;

export const EditProposalMutationSchema = z.object({
  proposal: EditProposalSchema,
});
export type EditProposalMutation = z.infer<typeof EditProposalMutationSchema>;

export const CollaboratorListSchema = z.object({
  collaborators: z.array(CollaboratorSchema),
});
export type CollaboratorList = z.infer<typeof CollaboratorListSchema>;
